/**
 * InviteModal — Modal for creating user invitations.
 *
 * Two-view state machine:
 *   1. Form view: email + role + message → Submit
 *   2. Success view: confirmation + invite link + Copy Link button
 *
 * Reuses existing modal CSS classes (modal, modal-content, etc.).
 * MUI Dialog handles focus containment, dismissal, and return to the opener.
 */
import React, { useState, useEffect, useId } from "react";
import Dialog from "@mui/material/Dialog";
import { createInvitation } from "./invitationService";
import { getAssignableRoles, getEffectiveInviteCap, ROLE_LABELS } from "./roleUtils";
import { showNotification } from "./notificationSystem";
import { Copy, Check } from "lucide-react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const InviteModal = ({ isOpen, setIsOpen, userData, onInviteCreated }) => {
  const id = useId();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [message, setMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successData, setSuccessData] = useState(null);
  const [copied, setCopied] = useState(false);

  const userRole = userData?.userRole || "standard";
  const assignableRoles = getAssignableRoles(userRole);
  const cap = getEffectiveInviteCap(userData);
  const sent = userData?.invitesSent || 0;
  const remaining = Math.max(0, cap - sent);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setEmail("");
      setRole(assignableRoles.length > 0 ? assignableRoles[0] : "");
      setMessage("");
      setError("");
      setSuccessData(null);
      setIsSubmitting(false);
      setCopied(false);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    setError("");

    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!EMAIL_RE.test(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }
    if (!role) {
      setError("Please select a role");
      return;
    }
    if (remaining <= 0) {
      setError("You have no invitations remaining");
      return;
    }

    setIsSubmitting(true);
    const result = await createInvitation({
      email: email.trim().toLowerCase(),
      role,
      message: message.trim() || undefined,
    });
    setIsSubmitting(false);

    if (result.success) {
      setSuccessData(result.data);
      if (onInviteCreated) onInviteCreated();
    } else {
      setError(result.error || "Failed to create invitation");
    }
  };

  const handleCopyLink = async () => {
    const link = successData?.inviteLink;
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      showNotification("Invite link copied to clipboard", "info", 3000);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showNotification("Failed to copy link", "error", 3000);
    }
  };

  const handleSendAnother = () => {
    setEmail("");
    setRole(assignableRoles.length > 0 ? assignableRoles[0] : "");
    setMessage("");
    setError("");
    setSuccessData(null);
    setCopied(false);
  };

  const handleClose = () => {
    if (!isSubmitting) setIsOpen(false);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      disableEscapeKeyDown={isSubmitting}
      aria-labelledby={`${id}-title`}
      maxWidth={false}
      sx={{ zIndex: 'var(--z-modal-backdrop)' }}
      slotProps={{ paper: {
        className: 'modal-content',
        style: {
          position: 'relative', top: 'auto', left: 'auto', transform: 'none',
          maxWidth: 480, width: 'calc(100% - 32px)', margin: 16,
          maxHeight: 'calc(100% - 32px)',
        },
      } }}
    >

          {/* ── Success View ── */}
          {successData ? (
            <>
              <h3 id={`${id}-title`} className="modal-title">
                {successData.emailSent ? "Invitation sent" : "Invitation created"}
              </h3>
              <div className="modal-form">
                <div className="modal-section">
                  <p style={{ margin: "0 0 16px 0", color: "var(--color-text-secondary)" }}>
                    {successData.emailSent ? (
                      <>We emailed an invitation to <strong>{successData.email || email}</strong>.</>
                    ) : (
                      <>We created an invitation for <strong>{successData.email || email}</strong>, but the email was not delivered.</>
                    )}
                  </p>

                  <p style={{ margin: "0 0 8px 0", color: "var(--color-text-secondary)" }}>
                    You can also share this link directly:
                  </p>
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "4px",
                  }}>
                    <input
                      type="text"
                      className="modal-input"
                      value={successData.inviteLink || ""}
                      readOnly
                      aria-label="Invitation link"
                      style={{ flex: 1, fontSize: "var(--text-sm)" }}
                    />
                    <button
                      className="button-confirm"
                      onClick={handleCopyLink}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        whiteSpace: "nowrap",
                        minWidth: "fit-content",
                      }}
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                      {copied ? "Copied" : "Copy link"}
                    </button>
                  </div>

                  {!successData.emailSent && <p style={{
                    margin: "12px 0 0 0",
                    fontSize: "var(--text-xs)",
                    color: "var(--color-text-secondary)",
                  }}>
                    {successData.emailDeliveryStatus === "not_configured"
                      ? "Email delivery is not configured in this environment. Share this link directly with the invitee."
                      : successData.emailDeliveryStatus === "blocked_by_policy"
                        ? "Staging email safety blocked delivery to this address. Use an approved QA inbox or share the link only with an approved staging tester."
                        : "Share this link directly or resend the invitation from My Account."}
                  </p>}
                </div>
              </div>

              <div className="modal-button-container">
                <button className="button-confirm" onClick={handleSendAnother} style={{ order: 0 }}>
                  Send another
                </button>
                <button className="button-cancel-secondary" onClick={handleClose} style={{ order: 1 }}>
                  Close
                </button>
              </div>
            </>
          ) : (
            /* ── Form View ── */
            <>
              <h3 id={`${id}-title`} className="modal-title">Invite User</h3>
              <div className="modal-form">
                {/* Remaining counter */}
                <div style={{
                  fontSize: "var(--text-sm)",
                  color: remaining > 0 ? "var(--color-text-secondary)" : "var(--color-error-text)",
                  marginBottom: "8px",
                }}>
                  {cap === Infinity ? 'Unlimited invitations' : `${remaining} of ${cap} invite${cap !== 1 ? 's' : ''} remaining`}
                </div>

                {/* Email */}
                <div className="modal-section">
                  <label htmlFor={`${id}-email`} className="modal-label" style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
                    Email Address
                  </label>
                  <input
                    id={`${id}-email`}
                    type="email"
                    className="modal-input"
                    placeholder="user@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isSubmitting}
                    autoFocus
                  />
                </div>

                {/* Role */}
                <div className="modal-section">
                  <label htmlFor={`${id}-role`} className="modal-label" style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
                    Role
                  </label>
                  <select
                    id={`${id}-role`}
                    className="modal-input"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    disabled={isSubmitting}
                  >
                    {assignableRoles.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABELS[r] || r}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message */}
                <div className="modal-section">
                  <label htmlFor={`${id}-message`} className="modal-label" style={{ fontSize: "var(--text-sm)", fontWeight: 600 }}>
                    Message <span style={{ fontWeight: 400, color: "var(--color-text-secondary)" }}>(optional)</span>
                  </label>
                  <textarea
                    id={`${id}-message`}
                    className="modal-input"
                    placeholder="Add a personal note..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                    disabled={isSubmitting}
                    rows={3}
                    style={{ resize: "vertical", fontFamily: "inherit" }}
                  />
                  <div style={{
                    fontSize: "var(--text-xs)",
                    color: "var(--color-text-secondary)",
                    textAlign: "right",
                    marginTop: "2px",
                  }}>
                    {message.length}/500
                  </div>
                </div>

                {/* Error */}
                {error && (
                  <div role="alert" style={{
                    color: "var(--color-error-text)",
                    backgroundColor: "var(--color-error-bg, #fef2f2)",
                    padding: "8px 12px",
                    borderRadius: "4px",
                    fontSize: "var(--text-sm)",
                  }}>
                    {error}
                  </div>
                )}
              </div>

              <div className="modal-button-container">
                <button className="button-cancel-secondary" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </button>
                <button
                  className="button-confirm"
                  onClick={handleSubmit}
                  disabled={isSubmitting || remaining <= 0}
                >
                  {isSubmitting ? "Sending..." : "Send Invite"}
                </button>
              </div>
            </>
          )}
    </Dialog>
  );
};

export default InviteModal;
